import React, { RefObject, useEffect, useRef, useState } from 'react';
import { Form, Input, Select } from 'antd';
import { FormInstance } from 'antd/es/form';
import { FormattedMessage, useIntl } from 'react-intl';
import { CANADA_STATES, USA_STATES } from '@constants';
import { checkInLatinLetters, FunctionalRootState } from '@share/utils';
import { countriesCode } from '@share/constants';
import { useDispatch, useSelector } from 'react-redux';
import { SelectValue } from 'antd/lib/select';
import { ICarsReviewBookState, setCarsStripe } from '@store/slices';
import { debounce, isEmpty } from 'lodash';

export interface FieldChange {
  name: string[]; // `name` es un array que contiene el nombre del campo
  errors: string[]; // `errors` es un array de mensajes de error
  touched: boolean; // `touched` indica si el campo fue tocado
  value?: any; // `value` es opcional y puede ser de cualquier tipo
  validating?: boolean; // `validating` es opcional
  warnings?: string[]; // `warnings` es opcional
  validated?: boolean; // `validated` es opcional
}


interface IProps {
  formRef: RefObject<FormInstance>;
  fullWidthForm?: boolean;
  useFormLabel?: boolean;
  isRequired: boolean;
  loading: boolean;
  handleBillingFormValidation: (billingForm: boolean) => void;
  multiplePayments?: any;
  setPaymentSelected?: (payment: string) => void;
  paymentSelected?: string;
  scrollToPaymentSelect?: boolean;
  scrollToPaymentSelectDisabled?: () => void;
  availableNewFlow?: boolean | undefined;
}

const BillingAddressForm: React.FC<IProps> = ({
  formRef,
  fullWidthForm,
  useFormLabel,
  isRequired,
  loading,
  handleBillingFormValidation,
  multiplePayments,
  setPaymentSelected,
  paymentSelected
}) => {
  const intl = useIntl();
  const MAX_LENGTH_NAME = 50;
  const MAX_POSTAL_CODE = 10;

  const [billing, setBilling] = useState({
    address: '',
    country: 'US',
    city: '',
    state: '',
    postalCode: '',
  });

  const dispatch = useDispatch();
  const carsReviewBookStore: ICarsReviewBookState = useSelector(
    (state: FunctionalRootState) => state.carsReviewBookStore,
  );
  const { stripe } = carsReviewBookStore;

  const setBillingData = (value: string | boolean, key: string) => {
    const updateCard = {
      ...stripe,
      [key]: value,
    } as any;

    dispatch(setCarsStripe(updateCard));
  };

  const onChangeAddressLine = (e: { target: HTMLInputElement }): void => {
    setBilling((prevState) => ({ ...prevState, address: e.target.value }));
    setBillingData(e.target.value, 'addressLine');
  };

  const onChangeCity = (e: { target: HTMLInputElement }): void => {
    setBilling((prevState) => ({ ...prevState, city: e.target.value }));
    setBillingData(e.target.value, 'city');
  };

  const onChangePostalCode = (e: { target: HTMLInputElement }): void => {
    setBilling((prevState) => ({ ...prevState, postalCode: e.target.value }));
    setBillingData(e.target.value, 'zipCode');
  };

const isUpdating = useRef(false);

const onChangeState = (value: SelectValue): void => {
  formRef.current?.setFieldsValue({ state: value });
  setBilling((prevState) => ({ ...prevState, state: value as string }));
  setBillingData(value as string, 'state');

  if (billing.country === 'US' || billing.country === 'CA') {
    formRef.current?.validateFields(['state']);
  }
};

const onChangeCountry = (value: string): void => {
  isUpdating.current = true;
  setBilling((prevState) => ({ ...prevState, country: value, state: '' }));
  setBillingData(value as string, 'country');
};

const [isFormValid, setIsFormValid] = useState(false);

const checkFormValidity = debounce(() => {
    if (!formRef.current) return;
  
    const hasErrors = formRef.current
      .getFieldsError()
      .some(({ errors }) => errors.length > 0);
  
    // Get the current field values
    const allFields = formRef.current.getFieldsValue();

    // Manually exclude saveCard and country from validation
    const filteredFields = Object.keys(allFields)
      .filter(key => key !== "country")
      .reduce((obj, key) => {
        obj[key] = allFields[key];
        return obj;
      }, {});
  
    const allFieldsFilled = Object.entries(filteredFields).every(([key, value]) => {
      return value !== undefined && value !== "";
    });
  
    // Update the button state
    setIsFormValid(!hasErrors && allFieldsFilled);
  }, 500);

useEffect(() => {
  handleBillingFormValidation(isFormValid);
}, [isFormValid]);

  const STATES = billing?.country === 'US' ? USA_STATES : CANADA_STATES;

  return (
    <div className={`payment-method`}>

      {
        (!isEmpty(paymentSelected) && paymentSelected !== 'card') && (
          <div>
            <Form
              name="billing_rule"
              className={`payment-method__rule ${fullWidthForm ? 'full-width' : ''}`}
              ref={formRef}
              onFieldsChange={checkFormValidity}
            >
              <div
                className={`payment-method__billing-address-wrapper ${useFormLabel ? 'no-labels' : ''}`}
              >
                <p className="payment-method__address-title">
                  <FormattedMessage id="billing.address" />:
                </p>

                <Form.Item
                  name="address"
                  className="address-line"
                  label={useFormLabel ? intl.formatMessage({ id: 'address' }) : null}
                  rules={[
                    {
                      required: isRequired,
                      message: intl.formatMessage({ id: 'error.message.address' }),
                    },
                  ]}
                >
                  <div className={`payment-method__input-wrapper ${useFormLabel ? 'no-labels' : ''}`}>
                    {!useFormLabel ? (
                      <span className="payment-method__input-label">
                        <FormattedMessage id="address" />
                      </span>
                    ) : null}
                    <div className="payment-method__input">
                      <Input
                        placeholder={intl.formatMessage({ id: 'address' })}
                        maxLength={MAX_LENGTH_NAME}
                        value={billing.address}
                        onChange={onChangeAddressLine}
                        disabled={loading}
                      />
                    </div>
                  </div>
                </Form.Item>

                <Form.Item
                  name="city"
                  // className={`billing-address ${
                  //   !(card?.country === 'US' || card?.country === 'CA') ? 'non-us-country' : ''
                  // }`
                  className={`billing-address`}
                  rules={[
                    {
                      required: isRequired,
                      message: intl.formatMessage({ id: 'error.message.city' }),
                      pattern: checkInLatinLetters,
                    },
                  ]}
                >
                  <div className={`payment-method__input-wrapper ${useFormLabel ? 'no-labels' : ''}`}>
                    {!useFormLabel ? (
                      <span className="payment-method__input-label">
                        <FormattedMessage id="city" />
                      </span>
                    ) : null}
                    <div className="payment-method__input">
                      <Input
                        placeholder={intl.formatMessage({ id: 'city' })}
                        maxLength={MAX_LENGTH_NAME}
                        value={billing.city}
                        onChange={onChangeCity}
                        disabled={loading}
                      />
                    </div>
                  </div>
                </Form.Item>

                <Form.Item name="country" className={`billing-address`}>
                  <div className={`payment-method__input-wrapper`}>
                    {!useFormLabel ? (
                      <span className="payment-method__input-label">
                        <FormattedMessage id="country" />
                      </span>
                    ) : null}
                    <div className="payment-method__input">
                      <Select
                        value={billing.country}
                        popupClassName="billing-country-select"
                        onChange={onChangeCountry}
                        optionFilterProp="label"
                        showSearch={true}
                        options={countriesCode.map((code) => ({
                          value: code.isoCode,
                          label: code.name,
                        }))}
                      ></Select>
                    </div>
                  </div>
                </Form.Item>

                {(billing.country === 'US' || billing.country === 'CA') && (
                  <Form.Item
                    name="state"
                    className="billing-address"
                    rules={[
                      {
                        required: isRequired,
                        message: intl.formatMessage({ id: 'error.message.state' }),
                        pattern: checkInLatinLetters,
                      },
                    ]}
                    initialValue={billing.state} // Agrega un valor inicial
                  >
                    <div className={`payment-method__input-wrapper ${useFormLabel ? 'no-labels' : ''}`}>
                      {!useFormLabel && (
                        <span className="payment-method__input-label">
                          <FormattedMessage id="state" />
                        </span>
                      )}
                      <div className="payment-method__input">
                        <Select
                          showSearch
                          value={billing.state} // Agrega `value` explícitamente
                          placeholder={intl.formatMessage({ id: 'state' })}
                          onChange={(value) => {
                            onChangeState(value);
                          }}
                          popupClassName="billing-state-select"
                          filterOption={(inputValue: string, option: any) => {
                            const inputLower = inputValue.toLowerCase();

                            return (
                              option.children.toString().toLowerCase().includes(inputLower) ||
                              option.value.toString().toLowerCase().includes(inputLower)
                            );
                          }}
                        >
                          {STATES.map(({ postalCode, name }) => (
                            <Select.Option key={postalCode} value={postalCode}>
                              {name}
                            </Select.Option>
                          ))}
                        </Select>
                      </div>
                    </div>
                  </Form.Item>
                )}

                <Form.Item
                  name="postalCode"
                  // className={`billing-address ${
                  //   !(card?.country === 'US' || card?.country === 'CA') ? 'non-us-country' : ''
                  // }`}
                  className={`billing-address`}
                  rules={[
                    {
                      required: isRequired,
                      message: intl.formatMessage({ id: 'error.message.postal.code' }),
                    },
                  ]}
                >
                  <div
                    className={`payment-method__input-wrapper postal-code-input ${useFormLabel ? 'no-labels' : ''
                      }`}
                  >
                    {!useFormLabel ? (
                      <span className="payment-method__input-label">
                        <FormattedMessage id="postal-code" />
                      </span>
                    ) : null}
                    <div className="payment-method__input">
                      <Input
                        placeholder={intl.formatMessage({ id: 'postal.code.label' })}
                        maxLength={MAX_POSTAL_CODE}
                        onChange={onChangePostalCode}
                        value={billing.postalCode}
                        disabled={loading}
                      />
                    </div>
                  </div>
                </Form.Item>
              </div>
            </Form>
          </div>
        )
      }
    </div>
  );
};

export default BillingAddressForm;
